import { Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuthStore } from '../../stores/authStore';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
}

/**
 * Determine the default landing page for a given role.
 * Used when redirecting unauthorized roles away from restricted routes.
 */
function defaultLandingPage(role: string): string {
  switch (role) {
    case 'super_admin':
    case 'admin':
      return '/admin';
    case 'regulator':
      return '/disclosures';
    default:
      // company_user, consultant_user, auditor, executive
      return '/dashboard';
  }
}

export default function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isAuthLoading = useAuthStore((state) => state.isAuthLoading);
  const user = useAuthStore((state) => state.user);

  // Show spinner while /auth/me is validating the stored token.
  // Prevents flash of protected content followed by redirect on expired token.
  if (isAuthLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={40} sx={{ color: '#6B3FA0' }} />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If allowedRoles specified, enforce role-based access
  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    return <Navigate to={defaultLandingPage(user.role)} replace />;
  }

  return <>{children}</>;
}
